// src/components/utils/Aleatorizador.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Snackbar,
    Alert,
    Divider,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { Refresh, ContentCopy, Search, Download } from "@mui/icons-material";
import axios from "axios";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";

function parseInput(raw) {
    if (!raw) return [];
    const tokens = raw
        .split(/[,;\n\r\t]+/)
        .flatMap((t) => t.split(/\s+/))
        .map((t) => t.trim())
        .filter(Boolean);
    return tokens;
}

function secureRandomInt(min, max) {
    const range = max - min + 1;
    const maxUnbiased = Math.floor(0x100000000 / range) * range - 1;
    let x;
    const buf = new Uint32Array(1);
    do {
        window.crypto.getRandomValues(buf);
        x = buf[0];
    } while (x > maxUnbiased);
    return min + (x % range);
}

function sampleWithoutReplacement(arr, k) {
    const n = arr.length;
    const res = [...arr];
    for (let i = 0; i < Math.min(k, n); i++) {
        const r = secureRandomInt(i, n - 1);
        [res[i], res[r]] = [res[r], res[i]];
    }
    return res.slice(0, Math.min(k, n));
}

/**
 * Extrae estadoId aunque venga como:
 * - estadoId / estado_id (number)
 * - estado (number)
 * - estado (obj {id, nombre})
 * - estadoId (string numérica)
 */
const extractEstadoId = (c) => {
    if (!c) return null;

    // 1) campos directos típicos
    const direct =
        c.estadoId ??
        c.estado_id ??
        c.estado_id_fk ??
        c.estado ?? // a veces el backend devuelve el id en "estado"
        null;

    // 2) si direct es objeto
    if (direct && typeof direct === "object") {
        const oid = direct.id ?? direct.estadoId ?? direct.estado_id ?? null;
        if (oid != null) return Number(oid);
    }

    // 3) si c.estado es objeto
    if (c.estado && typeof c.estado === "object") {
        const oid = c.estado.id ?? null;
        if (oid != null) return Number(oid);
    }

    // 4) si direct es string numérica
    if (direct != null && (typeof direct === "string" || typeof direct === "number")) {
        const n = Number(direct);
        return Number.isFinite(n) ? n : null;
    }

    return null;
};

// Normalizador de colegiado
const normColegiado = (c) => {
    if (!c) return null;
    const codigo = c.codigo ?? c.id ?? c.colegiadoId ?? c.colegiado_codigo ?? null;

    const nombre = c.nombre ?? "";
    const ap1 = c.apellido_1 ?? c.apellido1 ?? c.apellido ?? "";
    const ap2 = c.apellido_2 ?? c.apellido2 ?? "";
    const email = c.email ?? c.email_1 ?? "";
    const tel = c.telefono_1 ?? c.telefono ?? c.telefono1 ?? "";
    const tel2 = c.telefono_2 ?? c.telefono2 ?? "";

    const estadoId = extractEstadoId(c);

    return {
        codigo,
        nombreCompleto: [nombre, ap1, ap2].filter(Boolean).join(" "),
        email,
        telefono: tel,
        telefono2: tel2,
        estadoId, // <- NUEVO
    };
};

const Aleatorizador = () => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const { token } = useAuth();

    const [entrada, setEntrada] = useState("");
    const [cantidad, setCantidad] = useState(5);
    const [eliminarDuplicados, setEliminarDuplicados] = useState(true);

    const [resultado, setResultado] = useState([]); // códigos premiados
    const [mensaje, setMensaje] = useState(null);
    const [error, setError] = useState(null);

    const [tabla, setTabla] = useState([]); // premiados encontrados
    const [noEncontrados, setNoEncontrados] = useState([]); // premiados sin registro
    const [cargandoTabla, setCargandoTabla] = useState(false);

    // ======= NUEVO: estados =======
    const [estadosMap, setEstadosMap] = useState(new Map()); // id -> nombre
    const [cargandoEstados, setCargandoEstados] = useState(false);

    // ======= NUEVO: filtro estado > 6 =======
    const [excluirEstadosMayor6, setExcluirEstadosMayor6] = useState(false);

    const { lista, total, totalUnicos, hayDuplicados } = useMemo(() => {
        const tokens = parseInput(entrada);
        const total = tokens.length;
        const set = new Set(tokens);
        const totalUnicos = set.size;
        return {
            lista: eliminarDuplicados ? Array.from(set) : tokens,
            total,
            totalUnicos,
            hayDuplicados: total !== set.size,
        };
    }, [entrada, eliminarDuplicados]);

    const onSortear = () => {
        if (lista.length === 0) return setError("Introduce al menos 1 código.");
        if (!Number.isFinite(cantidad) || cantidad <= 0) return setError("La cantidad debe ser mayor que 0.");
        const k = Math.min(cantidad, lista.length);
        const muestra = sampleWithoutReplacement(lista, k);

        setResultado(muestra);
        setTabla([]);
        setNoEncontrados([]);
        setMensaje(`Sorteados ${k} códigos.`);
    };

    const onLimpiar = () => {
        setEntrada("");
        setResultado([]);
        setCantidad(5);
        setEliminarDuplicados(true);
        setTabla([]);
        setNoEncontrados([]);
        setExcluirEstadosMayor6(false);
    };

    const copiarResultado = async () => {
        try {
            const texto = resultado.join(", ");
            await navigator.clipboard.writeText(texto);
            setMensaje("Resultado copiado al portapapeles.");
        } catch {
            setError("No se pudo copiar al portapapeles.");
        }
    };

    // ======= NUEVO: cargar estados una vez =======
    useEffect(() => {
        const cargarEstados = async () => {
            setCargandoEstados(true);
            try {
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const res = await axios.get(`${API}/api/estados`, { headers });

                // Esperamos algo tipo [{id, nombre, ...}, ...]
                const arr = Array.isArray(res.data) ? res.data : [];
                const map = new Map();
                arr.forEach((e) => {
                    const id = e?.id ?? e?.estadoId ?? e?.estado_id ?? null;
                    const nombre = e?.nombre ?? e?.name ?? "";
                    if (id != null) map.set(Number(id), String(nombre || `Estado ${id}`));
                });

                setEstadosMap(map);
            } catch (e) {
                // No lo hacemos fatal, solo avisamos
                setError("No se pudieron cargar los estados (/api/estados).");
            } finally {
                setCargandoEstados(false);
            }
        };

        cargarEstados();
    }, [API, token]);

    const getEstadoNombre = (estadoId) => {
        if (estadoId == null) return "";
        const n = Number(estadoId);
        if (!Number.isFinite(n)) return "";
        return estadosMap.get(n) ?? `Estado ${n}`;
    };

    // Vista filtrada de tabla según el check
    const tablaFiltrada = useMemo(() => {
        if (!excluirEstadosMayor6) return tabla;
        return tabla.filter((r) => {
            const n = Number(r.estadoId);
            // si no hay estado, lo dejamos
            if (!Number.isFinite(n)) return true;
            return n <= 6;
        });
    }, [tabla, excluirEstadosMayor6]);

    // Busca SOLO los premiados en el backend y llena la tabla
    const buscarEnBackend = async () => {
        if (resultado.length === 0) return setError("Primero realiza el sorteo.");
        const codigos = Array.from(new Set(resultado)); // evita duplicados

        setCargandoTabla(true);
        setTabla([]);
        setNoEncontrados([]);

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const peticiones = codigos.map((cod) =>
            axios
                .get(`${API}/api/admin/colegiados/${encodeURIComponent(cod)}`, { headers })
                .then((res) => ({ ok: true, codigo: cod, data: res.data }))
                .catch(() => ({ ok: false, codigo: cod }))
        );

        const settled = await Promise.allSettled(peticiones);
        const hallados = [];
        const faltan = [];

        settled.forEach((r) => {
            if (r.status === "fulfilled") {
                const { ok, codigo, data } = r.value;
                if (ok && data) {
                    const n = normColegiado(data);
                    if (n && (n.codigo ?? codigo) != null) hallados.push(n);
                    else faltan.push(String(codigo));
                } else {
                    faltan.push(String(codigo));
                }
            }
        });

        // Mantén el orden según el resultado del sorteo
        const indexMap = new Map(resultado.map((c, i) => [String(c), i]));
        hallados.sort(
            (a, b) =>
                (indexMap.get(String(a.codigo)) ?? 0) - (indexMap.get(String(b.codigo)) ?? 0)
        );

        setTabla(hallados);
        setNoEncontrados(faltan);
        setCargandoTabla(false);

        if (hallados.length === 0) setError("No se encontró ninguno de los premiados.");
        else setMensaje(`Encontrados ${hallados.length} premiados.`);
    };

    // Exportar la tabla (premiados) a XLSX
    const exportarXLSX = () => {
        if (tablaFiltrada.length === 0) {
            setError("No hay datos para exportar. Pulsa 'Buscar en base de datos' primero.");
            return;
        }

        const rows = tablaFiltrada.map((r) => ({
            Código: r.codigo ?? "",
            "Nombre y apellidos": r.nombreCompleto ?? "",
            Estado: getEstadoNombre(r.estadoId) ?? "",
            Email: r.email ?? "",
            Teléfono: r.telefono ?? "",
            "Teléfono 2": r.telefono2 ?? "",
        }));

        const ws = XLSX.utils.json_to_sheet(rows);

        // Auto ancho de columnas
        const keys = Object.keys(rows[0] || {});
        const colWidths = keys.map((k) => {
            const maxLen = Math.max(
                k.length,
                ...rows.map((row) => String(row[k] ?? "").length)
            );
            return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
        });
        ws["!cols"] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Premiados");

        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const fname = `premiados_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
            now.getDate()
        )}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;

        XLSX.writeFile(wb, fname);
    };

    const disponibles = lista.length;
    const cantidadSugerida = Math.min(cantidad || 0, disponibles);

    // Para mostrar info del filtro
    const eliminadosPorFiltro = useMemo(() => {
        if (!excluirEstadosMayor6) return 0;
        return tabla.length - tablaFiltrada.length;
    }, [tabla.length, tablaFiltrada.length, excluirEstadosMayor6]);

    return (
        <Paper elevation={6} sx={{ p: 2.5, borderRadius: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Aleatorizador
                </Typography>
                <IconButton onClick={onLimpiar}>
                    <Refresh />
                </IconButton>
            </Stack>

            <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                Pega los códigos separados por <strong>comas</strong> (1,2,3) o en{" "}
                <strong>líneas</strong> independientes.
            </Typography>

            <TextField
                label="Listado de códigos"
                placeholder={"3\n9210\n9211\n...\nO también: 1,2,3,4"}
                multiline
                minRows={6}
                value={entrada}
                onChange={(e) => setEntrada(e.target.value)}
                fullWidth
                sx={{ mb: 1.5 }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                <TextField
                    label="Cantidad a sortear"
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(parseInt(e.target.value, 10))}
                    sx={{ width: 200 }}
                    inputProps={{ min: 1 }}
                />

                <FormControlLabel
                    control={
                        <Checkbox checked={eliminarDuplicados} onChange={(e) => setEliminarDuplicados(e.target.checked)} />
                    }
                    label="Eliminar duplicados"
                />

                <Stack direction="row" spacing={1} sx={{ ml: { sm: "auto" } }}>
                    <Button variant="contained" onClick={onSortear}>
                        Sortear
                    </Button>
                    <Button variant="outlined" onClick={buscarEnBackend} startIcon={<Search />}>
                        Buscar en base de datos
                    </Button>
                    <Tooltip title="Exportar premiados a Excel">
                        <span>
                            <IconButton onClick={exportarXLSX} disabled={tablaFiltrada.length === 0}>
                                <Download />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                <Chip label={`Total: ${total}`} />
                <Chip
                    label={`Únicos: ${totalUnicos}${hayDuplicados ? " (había duplicados)" : ""}`}
                    color={hayDuplicados ? "warning" : "default"}
                    variant={hayDuplicados ? "filled" : "outlined"}
                />
                <Chip label={`Disponibles ahora: ${disponibles}`} variant="outlined" />
                <Chip label={`A sortear: ${cantidadSugerida}`} variant="outlined" />
                {cargandoEstados ? (
                    <Chip label="Cargando estados…" variant="outlined" />
                ) : (
                    <Chip label={`Estados cargados: ${estadosMap.size}`} variant="outlined" />
                )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Resultado sorteo */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                        Resultado del sorteo ({resultado.length})
                    </Typography>
                    <IconButton onClick={copiarResultado} disabled={resultado.length === 0} title="Copiar">
                        <ContentCopy />
                    </IconButton>
                </Stack>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.default" }}>
                    {resultado.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Aún no hay resultados. Pulsa <strong>Sortear</strong>.
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            <Typography variant="body2">
                                <strong>CSV:</strong> {resultado.join(", ")}
                            </Typography>
                            <Divider />
                            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                {resultado.map((v, i) => (
                                    <li key={`${v}-${i}`}>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                            {v}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                        </Stack>
                    )}
                </Paper>
            </Box>

            {/* Tabla de premiados encontrados */}
            <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Coincidencias en base de datos (premiados)
                </Typography>

                {/* NUEVO: filtro estado > 6 */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={excluirEstadosMayor6}
                            onChange={(e) => setExcluirEstadosMayor6(e.target.checked)}
                        />
                    }
                    label="Excluir estados BAJA"
                />
            </Stack>

            {excluirEstadosMayor6 && tabla.length > 0 && (
                <Typography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.85 }}>
                    Filtrados por estado: se han excluido <strong>{eliminadosPorFiltro}</strong> premiados con estado &gt; 6.
                </Typography>
            )}

            {cargandoTabla ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2">Buscando colegiados…</Typography>
                </Stack>
            ) : null}

            {noEncontrados.length > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
                    No encontrados: {noEncontrados.join(", ")}
                </Typography>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Código</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Nombre y apellidos</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Estado</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Email</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Teléfono</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Teléfono 2</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {tablaFiltrada.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Sin datos. Pulsa <strong>Buscar en base de datos</strong> tras sortear.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tablaFiltrada.map((r) => (
                                <TableRow key={r.codigo}>
                                    <TableCell sx={{ fontFamily: "monospace" }}>{r.codigo}</TableCell>
                                    <TableCell>{r.nombreCompleto}</TableCell>
                                    <TableCell>
                                        {r.estadoId == null ? (
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                —
                                            </Typography>
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip size="small" label={String(r.estadoId)} variant="outlined" />
                                                <Typography variant="body2">{getEstadoNombre(r.estadoId)}</Typography>
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell>{r.email}</TableCell>
                                    <TableCell>{r.telefono}</TableCell>
                                    <TableCell>{r.telefono2}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Snackbars */}
            <Snackbar
                open={!!mensaje}
                autoHideDuration={2500}
                onClose={() => setMensaje(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setMensaje(null)} severity="success" sx={{ width: "100%" }}>
                    {mensaje}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={3500}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
                    {error}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default Aleatorizador;
